import { DatabasePercentageField } from '~/api-client/models/DatabasePercentageField';
import {
  ActivePermission,
  Address,
  Customer,
  CustomerShort,
  Database,
  DatabaseCalcField,
  DatabaseCalcFieldToken,
  DatabaseChecklistField,
  DatabaseCombinedTextField,
  DatabaseCreatedByField,
  DatabaseCreatedDateField,
  DatabaseCurrencyField,
  DatabaseDateField,
  DatabaseEntry,
  DatabaseEntryFileMetaData,
  DatabaseEntryStatisticsField,
  DatabaseField,
  DatabaseFieldType,
  DatabaseFieldVisibilityOperator,
  DatabaseFileField,
  DatabaseImageField,
  DatabaseModifiedByField,
  DatabaseModifiedDateField,
  DatabaseNumberField,
  DatabaseOwnerField,
  DatabasePeriodField,
  DatabaseTemplate,
  DatabaseUser,
  DatabaseYesNoField,
  HTTPRequestInit,
  Organisation,
  Product,
  ProductCategory,
  Purchase,
  RequestOpts,
  TimeUnit,
} from '~/api-client';
import { endOfDay, intlFormat, isValid, parseISO } from 'date-fns';
import { buildPurchasableFromProducts, Purchasable } from '~/modules/common/stores/products';
import _ from 'lodash';
import { isVisible } from '~/components/MyDb/Core/Views/composables/useVisibilityRules';

export function dateIsoStringToDate(dateString: string) {
  if (!!dateString) {
    return new Date(dateString);
  }

  return null;
}

export function getUserDisplayNameComplete(user: Customer) {
  if (!user) {
    return '';
  }

  let name = '';

  if (!!user.company) {
    name += user.company;

    if (!!user.firstName) {
      name += ' (' + user.firstName;

      if (!!user.lastName) {
        name += ' ' + user.lastName + ')';
      }
    } else if (!!user.lastName) {
      name += ' (' + user.lastName + ')';
    }
  } else {
    name = `${user.firstName} ${user.lastName}`;
  }

  return name;
}

export function getUserDisplayName(user: Customer) {
  if (!user) {
    return '';
  }

  let name = '';

  if (!!user.firstName) {
    name += user.firstName;

    if (!!user.lastName) {
      name += ' ' + user.lastName;
    }
  } else if (!!user.lastName) {
    name += user.lastName;
  }

  return name;
}

export function getDisplayNameFromAddress(address: Address) {
  if (!address) {
    return '';
  }

  if (!!address.companyName) {
    return address.companyName;
  }

  return `${address.firstName} ${address.lastName}`;
}


export function getUserSortName(user: Customer | CustomerShort | undefined) {
  if (!user) {
    return '';
  }

  if (!!user.company) {
    return user.company.toLowerCase();
  }

  return `${user.lastName}${user.firstName}`.toLowerCase();
}

export function getValidPurchases(purchases: Array<Purchase>, type?: EditionType): Array<Purchase> {
  return purchases.filter(p => (!!type ? p.editionType === type : true));
}

export function getValidNextPurchasesAfterNow(purchases: Array<Purchase>, type?: EditionType): Array<Purchase> {
  const today = endOfDay(new Date()).valueOf();

  return purchases.filter(p => (!!type ? p.editionType === type : true) && (!!p.edition?.releaseDate && endOfDay(new Date(p.edition?.releaseDate!!)).valueOf() >= today));
}

export function adjustTimeToNoon(date: Date | undefined) {
  if (!date) {
    return date;
  }

  const result = new Date(date);
  result.setHours(12, 0, 0, 0);
  return result;
}

export function compareArray<T>(arr1: Array<T>, arr2: Array<T>, compFn?: (obj1: T, obj2: T) => boolean) {
  if (!!arr1 && !arr2) {
    return false;
  }

  if (!arr1 && !!arr2) {
    return false;
  }

  if (!arr1 && !arr2) {
    return false;
  }

  if (arr1.length != arr2.length) {
    return false;
  }

  const compareFn = !compFn ? (obj1: T, obj2: T) => obj1 === obj2 : compFn;

  for (let i = 0; i < arr1.length; i++) {
    const obj1 = arr1[i];
    const obj2 = arr2[i];

    if (!compareFn(obj1, obj2)) {
      return false;
    }
  }
  return true;
}

export function dateStringProver(dateString: String) {

  let tempDateString = dateString.replaceAll(/[&\/\\#,+()$~%.'":*?<>{}-]/g, '');

  let tempDatStringArray = tempDateString.split(/(\s+)/).filter(e => e.trim().length > 0);

  let tempStringExport = '';


  let counter1 = 0;
  let counter2 = 0;
  let counter3 = 0;

  while (counter1 < tempDatStringArray.length) {

    while (counter2 < monthNames.length) {
      if (monthNames[counter2].includes(tempDatStringArray[counter1])) {
        tempDatStringArray[counter1] = monthNames[counter2];
      }

      counter2++;
    }

    counter2 = 0;
    counter1++;
  }


  while (counter3 < tempDatStringArray.length) {

    tempStringExport = tempStringExport + tempDatStringArray[counter3];

    if (counter3 < (tempDatStringArray.length - 1)) {
      tempStringExport = tempStringExport + ' ';
    }

    counter3++;
  }

  return tempStringExport;
}

export function hasStarted(date: Date | undefined) {
  if (!date) {
    return false;
  }

  const todayDate = new Date();
  const startingDate = new Date(date);
  return todayDate > startingDate;
}

export function isExpired(date: Date | undefined) {
  if (!date) {
    return false;
  }

  const todayDate = new Date();
  const expirationDate = new Date(date);
  return todayDate > expirationDate;
}

export function getOrganisationName(orga: Organisation | undefined, type: String) {
  if (!orga) {
    return '';
  }
  if (type === 'code') return orga.code;
  if (type === 'name') return orga.name;
}

export function getSearchDate(date?: Date) {
  if (!date) {
    return '01 01 1970';
  }

  const day = ('00' + new Date(date).getDate()).slice(-2);
  const month = monthNames[(new Date(date).getMonth())];
  const year = new Date(date).getFullYear();

  return day + ' ' + month + ' ' + year;
}

export function createRequestOptionFunction(headers: HeadersInit) {
  return (requestContext: { init: HTTPRequestInit, context: RequestOpts }) => {
    let finalHeaders: HeadersInit = {};

    if (requestContext.init && requestContext.init.headers) {
      finalHeaders = { ...requestContext.init.headers };
    }

    finalHeaders = {
      ...finalHeaders,
      ...headers,
    };

    return Promise.resolve({ headers: finalHeaders });
  };
}

export function handleFileDownload(data: Blob, filename: string) {
  let fileURL = window.URL.createObjectURL(data);
  let fileLink = document.createElement('a');

  fileLink.href = fileURL;
  fileLink.setAttribute('download', filename);
  document.body.appendChild(fileLink);
  fileLink.click();
  document.body.removeChild(fileLink);
}

export function handleFileOpenInTab(data: any) {
  let fileURL = window.URL.createObjectURL(new Blob([data]));
  window.open(fileURL, '_blank');
}

export async function buildImageUrlFromBlob(binaryData: Blob | undefined) {
  if (!binaryData) {
    return undefined;
  }

  if (process.server) {
    return undefined;// buildImageUrlFromBlobOnServerSide(binaryData);
  }

  if (!!binaryData && binaryData.size > 0) {
    return URL.createObjectURL(binaryData);
  }

  return undefined;
}

async function buildImageUrlFromBlobOnServerSide(blob: Blob) {
  let avatarUrl = undefined;
  let binaryData = Buffer.from(await blob.arrayBuffer());

  if (!!binaryData && binaryData.byteLength > 0) {
    const base64 = imageToBase64(binaryData);
    avatarUrl = `data:${blob.type};base64,` + base64;
  }

  return avatarUrl;
}

function imageToBase64(buf: Buffer) {
  return Buffer.from(buf, 'binary').toString('base64');
}

export function userCanUpdate(user: Customer | undefined, permissions: ActivePermission, owner?: Customer | string) {
  const ownerId = typeof owner === 'object' ? owner.id : owner;
  if (user && ownerId) {
    return user.id === ownerId && permissions.updateOwnedEntries || user.id !== ownerId && permissions.updateForeignEntries;
  }
  if (user) {
    return permissions.updateOwnedEntries || permissions.updateForeignEntries;
  }
  return false;
}

export function userCanDelete(user: Customer | undefined, permissions: ActivePermission, owner?: Customer | string) {
  const ownerId = typeof owner === 'object' ? owner.id : owner;
  if (user && ownerId) {
    return user.id === ownerId && permissions.deleteOwnedEntries || user.id !== ownerId && permissions.deleteForeignEntries;
  }
  if (user) {
    return permissions.deleteOwnedEntries || permissions.deleteForeignEntries;
  }
  return false;
}

export function userCanRead(user: Customer | undefined, permissions: ActivePermission, owner?: Customer | string) {
  const ownerId = typeof owner === 'object' ? owner.id : owner;
  if (user && ownerId) {
    return user.id === ownerId && permissions.readOwnedEntries || user.id !== ownerId && permissions.readForeignEntries;
  }
  if (user) {
    return permissions.readOwnedEntries || permissions.readForeignEntries;
  }
  return false;
}

export function userCanAdd(user: Customer, permissions: ActivePermission) {
  return permissions.create;
}

export function userIsDbOwner(user: Customer, database: Database) {
  const ownerId = database.owner?.id;
  return user.id === ownerId;
}

export const calculateUnitDayTime = (unitAmount: number, unitTime: TimeUnit) => {
  switch (unitTime) {
    case 'DAY':
      return unitAmount;
    case 'WEEK':
      return unitAmount * 7;
    case 'MONTH':
      return unitAmount * 30;
    case 'YEAR':
      return unitAmount * 365;
  }
};

export function initializeProductsUtil(result: Product[]): Array<any> {

  if (!result) {
    throw new Error('unable to load products');
  }
  // build purchasable arrays by category

  const productsByCategory: { [category: string]: Product[] } = {};
  const productById: { [id: string]: Product } = {};
  const categoryById: { [id: string]: ProductCategory } = {};

  result.forEach((key: Product) => {
    const category = key.productCategory!;

    let products = productsByCategory[category.valueOf()];

    if (!products) {
      products = [];
      productsByCategory[category.valueOf()] = products;
    }
    categoryById[key.id!] = key.productCategory!;
    productsByCategory[category.valueOf()].push(key);
    productById[key.id!] = key;
  });

  const purchasablesByCategory: { [category: string]: Purchasable[] } = {};

  Object.keys(productsByCategory).forEach(category => {
    purchasablesByCategory[category] = buildPurchasableFromProducts(productsByCategory[category]);
  });

  const purchasableById: { [id: string]: Purchasable } = {};

  Object.keys(purchasablesByCategory).forEach(category => {
    purchasablesByCategory[category].forEach(purchasable => {
      purchasableById[purchasable.id] = purchasable;
    });
  });

  const categories = Object.keys(purchasablesByCategory);

  return [productsByCategory, productById, purchasablesByCategory, purchasableById, categories, categoryById];
}

export const getDaysDiff = (start: Date, end: Date) => {
  const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
  return Math.round(Math.abs((end.getTime() - start.getTime()) / oneDay));
};

export enum SetDateUnit {DAYS = 'DAYS', MONTHS = 'MONTHS'}

export const dateForRow = (date: Date, dateUnit?: SetDateUnit, monthsToAdd: number = 0, daysToAdd: number = 0, asDate: boolean = false) => {
  const { $i18n } = useNuxtApp();
  let dateToReturn;
  if (!!date) {
    if (!!dateUnit) {
      switch (dateUnit) {
        case SetDateUnit.DAYS:
          dateToReturn = new Date(_.cloneDeep(date).setDate(date.getDate() + daysToAdd));
          break;
        case SetDateUnit.MONTHS:
          dateToReturn = new Date(_.cloneDeep(date).setMonth(date.getMonth() + monthsToAdd));
          break;
      }
    } else {
      dateToReturn = new Date(date);
    }
    if (asDate) {
      return dateToReturn;
    }
    return $i18n.d(dateToReturn, 'short');

  }
  return ' - ';
};

export function errorMsg(error: any, defaultMessage?: string): Promise<string> {
  if (error.response?.body) {
    const reader = error.response.body.getReader();

    return reader.read().then((status: any) => {
      const error: string = new TextDecoder().decode(status.value);
      return error;
    });
  }

  return Promise.resolve(defaultMessage ?? 'Es ist ein unbekannter Fehler aufgetreten.');
}

export type GroupedEntries = { [key: string]: DatabaseEntry[] };

export const groupBy = (database: Database, data: DatabaseEntry[]) => {
  const groupByFieldId = database.settings!.groupBy;
  const field = database.fields?.find(f => f.id === groupByFieldId)!;
  return groupByField(field, data);
};

export const groupByField = (field: DatabaseField, data: DatabaseEntry[]) => {
  const displayFn = getDisplayValueFn(field);
  return data.reduce((groups, currentEntry) => {
    const value = currentEntry.fieldsValues?.[field.id!] ?? '';
    const key = displayFn(value, currentEntry);
    const group = groups[key];
    if (!group) {
      groups[key] = [];
    }
    groups[key].push(currentEntry);
    return groups;
  }, {} as GroupedEntries);
};

// TODO move to useFormatter
export const formattedNumber = (value: number | bigint | undefined, precision = 0, maxPrecision = precision) => {
  return typeof value === 'number' ? new Intl.NumberFormat(undefined, {
    maximumFractionDigits: maxPrecision,
    minimumFractionDigits: precision,
  }).format(value) : '';
};

// TODO move to useFormatter
export const formattedCurrency = (value: number | bigint | undefined, currency: string, precision = 2) => {
  return typeof value === 'number' ? new Intl.NumberFormat(undefined, {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
    style: 'currency',
    currency: currency ?? '',
    currencyDisplay: 'code',
  }).format(value) : '';
};

// TODO move to useFormatter
export const formattedPercentage = (value: number | bigint | undefined, precision = 0) => {
  return typeof value === 'number' ? new Intl.NumberFormat(undefined, {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
    style: 'percent',
  }).format(value) : '';
};

type UserFields = 'username' | 'firstName' | 'lastName';
const formatUserField = (user: DatabaseUser, field: UserFields, useBrackets: boolean): string => {
  const value = user[field];
  if (!value) return '';
  return useBrackets && field === 'username' ? `(${value})` : value;
};

const createUserDisplayFunction = (fields: UserFields[]) => {
  return (value: any, entry: DatabaseEntry | undefined): string => {
    const dbUser = entry?.updatedBy ?? value as DatabaseUser | undefined;
    if (!dbUser) return '';
    const useBrackets = fields.includes('username') && fields.length > 1;
    const orderedFields: UserFields[] = ['firstName', 'lastName', 'username'];
    return orderedFields
      .filter(f => fields.includes(f) && dbUser[f])
      .map(field => formatUserField(dbUser, field, useBrackets))
      .join(' ');
  };
};

const evaluateAsJavascript = (expression: string) => {
  const getOrDefault = (val: any, def: any) => {
    return def;
  };
  // Create a function that evaluates the expression in a controlled environment
  const evaluate = new Function('getOrDefault', `try {
        return ${expression};
    } catch (e) {
        return {error: e.message};
    }`);
  return evaluate(getOrDefault);
};

export const getDisplayValueFn = (fieldDef: DatabaseField, database?: Database | DatabaseTemplate): ((value: any, entry: DatabaseEntry | undefined, additionalData?: any) => string | number) => {
  switch (fieldDef.fieldType) {

    case DatabaseFieldType.CalcField: {
      const fd = fieldDef as DatabaseCalcField;

      const stringifyTokens: any = (tokens: DatabaseCalcFieldToken[]) => tokens.map((token) => {
        if (token.type === 'FIELD') {
          const f = database?.fields?.find((f) => f.id === token.value);
          if (f?.fieldType === DatabaseFieldType.PercentageField) {
            return `(+getOrDefault('${token.value}', 0) / 100)`; // convert percentage to decimal
          }
          if (f?.fieldType === DatabaseFieldType.CategoryField) {
            return `+getOrDefault('${token.value}', 0, true)`;
          }
          if (f?.fieldType === DatabaseFieldType.CalcField) {
            return (stringifyTokens((f as DatabaseCalcField).formula ?? []) as string[]).join(' ');
          }
          return `getOrDefault('${token.value}', 0)`;
        } else {
          return token.value;
        }
      });

      const formula = fd.formula ? stringifyTokens(fd.formula).join(' ') : '0';
      const evaluationResult = evaluateAsJavascript(formula);

      let evaluationFunction: Function;
      evaluationFunction = new Function('context', `
        const getOrDefault = (val, def, forceNumber = false) => (typeof context[val] === 'number' || forceNumber) ? context[val] ?? def : def;
            const result = ${formula};
            if (isNaN(result)) {
                return 0;
            }
            return result;
    `);

      return (value: any, entry: DatabaseEntry | undefined) => {
        if (evaluationResult && evaluationResult.error) {
          return 'Formelfehler!';
        }

        const evaluated = evaluationFunction(entry?.fieldsValues ?? {});
        if (['EUR', 'USD', 'GBP'].includes(fd.suffix ?? '')) {
          return formattedCurrency(evaluated, fd.suffix ?? '', fd.precision);
        }
        if (['PERCENT'].includes(fd.suffix ?? '')) {
          formattedPercentage(evaluated, fd.precision ?? 0);
        }
        return formattedNumber(evaluated, fd.precision ?? 0);
      };
    }

    case DatabaseFieldType.CategoryField: {
      return (value: any) => Array.isArray(value) ? value.join(', ') : value;
    }
    case DatabaseFieldType.CombinedTextField: {
      const fd = fieldDef as DatabaseCombinedTextField;

      const stringifyTokens: any = (tokens: DatabaseCalcFieldToken[]) => tokens.map((token) => {
        if (token.type === 'FIELD') {
          const f = database?.fields?.find((f) => f.id === token.value);
          if (f?.fieldType === DatabaseFieldType.CombinedTextField) {
            return (`visible('${f.id}') && [${(stringifyTokens((f as DatabaseCombinedTextField).formula ?? []) as string[]).join(', ')}]`);
          }
          return `visible('${token.value}') && getOrDefault('${token.value}', '')`;
        } else {
          return `'${token.value}'`;
        }
      });

      const formula = fd.formula ? stringifyTokens(fd.formula).join(',') : '0';
      const evaluationFunction = new Function('context', `
        const visible = (f) => context.isVisible(context.database.fields.find((field) => field.id === f), context.database, context.entry);
        const getOrDefault = (val, def) => typeof context[val] === 'string' ? context[val] : def;
        const result = [${formula}].flat();
        return result.filter((s) => !!s).join(' ');
      `);
      return (value: any, entry: DatabaseEntry | undefined) => {
        return evaluationFunction({ isVisible: isVisible, database: database, entry: entry, ...entry?.fieldsValues });
      };
    }
    case DatabaseFieldType.CreatedByField: {
      const fields = ((fieldDef as DatabaseCreatedByField).userFields?.filter(f => f !== 'avatar') ?? []) as UserFields[];
      return createUserDisplayFunction(fields);
    }
    case DatabaseFieldType.OwnerField: {
      const fd = fieldDef as DatabaseOwnerField;
      const fields = (fd.ownerFields?.filter((f) => f !== 'avatar') ?? []) as ('username' | 'firstName' | 'lastName')[];
      return (value: any, entry: DatabaseEntry | undefined) => {
        const dbUser = entry?.owner ?? value as DatabaseUser | undefined;
        if (dbUser) {
          return fields.map((f) => f === 'username' ? `(${dbUser.username})` : dbUser[f]).filter((part) => !!part).join(' ');
        }
        return '';
      };
    }
    case DatabaseFieldType.CreatedDateField: {
      const fd = fieldDef as DatabaseCreatedDateField;
      return (value: any, entry: DatabaseEntry | undefined) => {
        const timestamp = entry?.created ?? value;
        if (!timestamp || !isValid(timestamp)) {
          return '';
        }
        if (fd.appearance === 'DATE_FIELD_ONLY_DATE_LOOK') {
          return intlFormat(timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }, { locale: 'de-DE' });
        } else if (!fd.appearance || fd.appearance === 'DATE_FIELD_DATE_AND_TIME_LOOK') {
          return intlFormat(timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: 'numeric',
          }, { locale: 'de-DE' });
        } else if (fd.appearance === 'DATE_FIELD_ONLY_TIME_LOOK') {
          return intlFormat(timestamp, {
            hour: 'numeric',
            minute: 'numeric',
          }, { locale: 'de-DE' });
        } else {
          return '';
        }
      };
    }
    case DatabaseFieldType.CurrencyField: {
      const fd = fieldDef as DatabaseCurrencyField;
      return (value: any, entry: DatabaseEntry | undefined) => formattedCurrency(value, fd.type ?? '');
    }
    case DatabaseFieldType.PeriodField:
    case DatabaseFieldType.DateField: {
      const fd = fieldDef.fieldType == DatabaseFieldType.DateField ? fieldDef as DatabaseDateField : fieldDef as DatabasePeriodField;
      const getDisplayValue = (value: string) => {
        if (!value) {
          return '';
        }
        if (['PERIOD_FIELD_ONLY_YEAR_LOOK', 'PERIOD_FIELD_PLAIN_STRING_LOOK'].includes(fd.type!)) {
          return value;
        }
        const timestamp = parseISO(value);
        if (!isValid(timestamp)) {
          return '';
        }
        if (fd.type === 'DATE_FIELD_ONLY_DATE_LOOK') {
          return intlFormat(timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }, { locale: 'de-DE' });
        } else if (fd.type === 'DATE_FIELD_DATE_AND_TIME_LOOK') {
          return intlFormat(timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: 'numeric',
            second: fd.withSeconds ? 'numeric' : undefined,
          }, { locale: 'de-DE' });
        } else if (fd.type === 'DATE_FIELD_ONLY_TIME_LOOK') {
          return intlFormat(timestamp, {
            hour: 'numeric',
            minute: 'numeric',
            second: fd.withSeconds ? 'numeric' : undefined,
          }, { locale: 'de-DE' });
        } else {
          return '';
        }
      };
      return (value: any, entry: DatabaseEntry | undefined) => {
        if (fd.fieldType === DatabaseFieldType.DateField) {
          return getDisplayValue(value as string);
        }
        if (fd.fieldType === DatabaseFieldType.PeriodField) {
          const values = value?.split('#');
          return values?.map(getDisplayValue).join(' - ');
        }
      };
    }
    case DatabaseFieldType.EmailField: {
      return (value: any, entry: DatabaseEntry | undefined) => value as string;
    }
    case DatabaseFieldType.LinkField: {
      return (value: any, entry: DatabaseEntry | undefined) => value as string;
    }
    case DatabaseFieldType.ModifiedByField: {
      const fields = ((fieldDef as DatabaseModifiedByField).userFields?.filter(f => f !== 'avatar') ?? []) as UserFields[];
      return createUserDisplayFunction(fields);
    }
    case DatabaseFieldType.ModifiedDateField: {
      const fd = fieldDef as DatabaseModifiedDateField;
      return (value: any, entry: DatabaseEntry | undefined) => {
        const timestamp = entry?.updated ?? value;
        if (!timestamp || !isValid(timestamp)) {
          return '';
        }
        if (fd.appearance === 'DATE_FIELD_ONLY_DATE_LOOK') {
          return intlFormat(timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }, { locale: 'de-DE' });
        } else if (!fd.appearance || fd.appearance === 'DATE_FIELD_DATE_AND_TIME_LOOK') {
          return intlFormat(timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: 'numeric',
          }, { locale: 'de-DE' });
        } else if (fd.appearance === 'DATE_FIELD_ONLY_TIME_LOOK') {
          return intlFormat(timestamp, {
            hour: 'numeric',
            minute: 'numeric',
          }, { locale: 'de-DE' });
        } else {
          return '';
        }
      };
    }
    case DatabaseFieldType.NumberField: {
      const fd = fieldDef as DatabaseNumberField;
      return (value: any, entry: DatabaseEntry | undefined) => formattedNumber(value, fd.precision);
    }
    case DatabaseFieldType.PasswordField: {
      return (value: any, entry: DatabaseEntry | undefined) => value as string;
    }
    case DatabaseFieldType.PercentageField: {
      const fd = fieldDef as DatabasePercentageField;
      return (value: any, entry: DatabaseEntry | undefined) => formattedPercentage(value, 0);
    }
    case DatabaseFieldType.RatingField: {
      return (value: any, entry: DatabaseEntry | undefined) => value as string;
    }
    case DatabaseFieldType.TelnumberField: {
      return (value: any, entry: DatabaseEntry | undefined) => value as string;
    }
    case DatabaseFieldType.ChecklistField: {
      const fd = fieldDef as DatabaseChecklistField;
      return (value: any, entry: DatabaseEntry | undefined) => {
        const checked = value?.filter((e) => e.checked).length ?? 0;
        const overall = value?.length ?? 0;
        return `${checked}/${overall}`;
      };
    }
    case DatabaseFieldType.YesNoField: {
      const fd = fieldDef as DatabaseYesNoField;
      return (value: any, entry: DatabaseEntry | undefined) => {
        if (!!value && fd.activeText) {
          return fd.activeText;
        }
        if (!!!value && fd.inactiveText) {
          return fd.inactiveText;
        }
        return value;
      };
    }
    case DatabaseFieldType.ImageField: {
      const fd = fieldDef as DatabaseImageField;
      return (value: DatabaseEntryFileMetaData, entry: DatabaseEntry | undefined, additionaData?: any) => {
        const baseUrl = additionaData?.baseUrl;
        if (value) {
          return `${baseUrl}/db-media/${database!.id}/${entry?.id}/${fd.id}/${value.resourceIdentifier}`;
        }
        return '';
      };
    }
    case DatabaseFieldType.FileField: {
      const fd = fieldDef as DatabaseFileField;
      return (value: DatabaseEntryFileMetaData, entry: DatabaseEntry | undefined, additionaData?: any) => {
        const baseUrl = additionaData?.baseUrl;
        return `${baseUrl}/db-media/${database!.id}/${entry?.id}/${fd.id}/${value.resourceIdentifier}`;
      };
    }
    case DatabaseFieldType.StatisticsField: {
      const fd = fieldDef as DatabaseEntryStatisticsField;
      return (value: any, entry: DatabaseEntry | undefined) => {
        return entry?.statistics?.viewCount ?? 0;
      };
    }
    default: {
      return (value: any) => value as string;
    }
  }
};

export const COMPARISON_OPERATOR_MAP = new Map<DatabaseFieldType, Map<DatabaseFieldVisibilityOperator, (val1: unknown, val2: unknown) => boolean>>([
  [DatabaseFieldType.CreatedByField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => (val1 as Customer)?.id === (val2 as Customer)?.id],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => (val1 as Customer)?.id !== (val2 as Customer)?.id],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
  [DatabaseFieldType.CreatedDateField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => (val1 as Date) === (val2 as Date)],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => (val1 as Date) !== (val2 as Date)],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => (val1 as Date) < (val2 as Date)],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => (val1 as Date) > (val2 as Date)],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
  [DatabaseFieldType.ModifiedByField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => (val1 as Customer)?.id === (val2 as Customer)?.id],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => (val1 as Customer)?.id !== (val2 as Customer)?.id],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
  [DatabaseFieldType.ModifiedDateField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => (val1 as Date) === (val2 as Date)],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => (val1 as Date) !== (val2 as Date)],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => (val1 as Date) < (val2 as Date)],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => (val1 as Date) > (val2 as Date)],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
  [DatabaseFieldType.CalcField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
  [DatabaseFieldType.OwnerField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => (val1 as Customer)?.id === (val2 as Customer)?.id],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => (val1 as Customer)?.id !== (val2 as Customer)?.id],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
  [DatabaseFieldType.RatingField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.ImageField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.ChecklistField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined || (val1 as any[]).length > 0],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (val1 as any[]).length === 0],
  ])],
  [DatabaseFieldType.FileField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.DateField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => (val1 as Date) < (val2 as Date)],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => (val1 as Date) > (val2 as Date)],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.EmailField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => (val1 as string)?.includes(val2 as string)],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => (val1 as string)?.startsWith(val2 as string)],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined && (val1 as string).length > 0],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (val1 as string).length === 0],
  ])],
  [DatabaseFieldType.YesNoField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.CategoryField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1: string | string[], val2: string[]) => Array.isArray(val1) ? val1.length === val2.length && val1.every((val) => val2.includes(val)) : val2.length === 1 && val2.includes(val1)],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1: string | string[], val2: string[]) => Array.isArray(val1) ? val1.length !== val2.length || !val1.every((val) => val2.includes(val)) : val2.length !== 1 || !val2.includes(val1)],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined && (Array.isArray(val1) ? val1.length > 0 : !!val1)],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (Array.isArray(val1) ? val1.length === 0 : !val1)],
  ])],
  [DatabaseFieldType.LinkField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => (val1 as string)?.includes(val2 as string)],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => (val1 as string)?.startsWith(val2 as string)],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined && (val1 as string).length > 0],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (val1 as string).length === 0],
  ])],
  [DatabaseFieldType.NumberField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => (val1 as number) < (val2 as number)],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => (val1 as number) > (val2 as number)],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.PasswordField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined && (val1 as string)?.length > 0],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (val1 as string)?.length === 0],
  ])],
  [DatabaseFieldType.PercentageField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => (val1 as number) < (val2 as number)],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => (val1 as number) > (val2 as number)],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.TelnumberField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => (val1 as string)?.includes(val2 as string)],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => (val1 as string)?.startsWith(val2 as string)],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined && (val1 as string)?.length > 0],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (val1 as string)?.length === 0],
  ])],
  [DatabaseFieldType.TextField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => (val1 as string)?.includes(val2 as string)],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => (val1 as string)?.startsWith(val2 as string)],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined && (val1 as string)?.length > 0],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined || (val1 as string)?.length === 0],
  ])],
  [DatabaseFieldType.VideoField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.CurrencyField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => val1 === val2],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => val1 !== val2],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => (val1 as number) < (val2 as number)],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => (val1 as number) > (val2 as number)],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.PeriodField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => val1 !== null && val1 !== undefined],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => val1 === null || val1 === undefined],
  ])],
  [DatabaseFieldType.CombinedTextField, new Map([
    [DatabaseFieldVisibilityOperator.Includes, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.StartsWith, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Equal, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEqual, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.LessThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.GreaterThan, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.NotEmpty, (val1, val2) => false],
    [DatabaseFieldVisibilityOperator.Empty, (val1, val2) => false],
  ])],
]);

export const getTitleSuffix = (fieldDef: DatabaseField): ((entry: DatabaseEntry | undefined, view: boolean) => string | number | null) => {
  switch (fieldDef.fieldType) {
    case DatabaseFieldType.ChecklistField: {
      const fd = fieldDef as DatabaseChecklistField;
      return (entry: DatabaseEntry | undefined, view: boolean) => {
        const value = entry?.fieldsValues?.[fieldDef.id!] ?? [];
        const checked = value?.filter((v: any) => !!v.checked).length ?? 0;
        const overall = value?.length ?? 0;
        return `(${checked}/${overall})`;
      };
    }
    default: {
      return (value: any) => '';
    }
  }
};

export const getFieldName = (field: DatabaseField, entry: DatabaseEntry, db: Database, view: boolean, titleAsFallback = false) => {
  const title = field.hideTitle || db.hideFieldTitles ? '' : (field.showTitleAsPhrase ? field.titlePhrase ?? (titleAsFallback ? field.name : '') : field.name);
  return [title, getTitleSuffix(field)(entry, view)].filter((x) => !!x).join(' ');
};

export const isContactField = (field: DatabaseField) => ['OWNER_FIELD', 'CREATED_BY_FIELD', 'MODIFIED_BY_FIELD'].includes(field.fieldType);

export const sortConditionsFromSortObject = (obj: {
  [key: string]: string
}) => !!obj ? Object.entries(obj).filter(([field, value]) => !!value).map(([field, value]) => `${field}:${value}`) : undefined;

export const filterConditionsFromFilterObject = (obj: {
  [key: string]: string | string[]
}) => !!obj ? Object.entries(obj)
  .filter(([field, value]) => value !== null && value !== undefined && value !== '' && (Array.isArray(value) ? value.length > 0 : true))
  .map(([field, value]) => `${field}:${Array.isArray(value) ? value.filter((val) => !!val).join('|') : value}`) : undefined;

interface TempUserHolder {
  user: CustomerShort,
  organisationCode?: string | undefined
}

export const assignOrganisation = (currentUser: Ref<TempUserHolder>, row: CustomerShort, assignOrganisationDialogVisible: Ref<boolean>) => {
  currentUser.value = {
    user: row,
    organisationCode: !!row.organisation ? row.organisation.code : undefined,
  };
  assignOrganisationDialogVisible.value = true;
};

export const findOrganisationName = (customer: CustomerShort) => {
  if (!!customer.organisation) {
    return customer.organisation.name;
  }
  return `-`;
};

export const deepCopyObject = (obj: any) => {
  let rv;

  switch (typeof obj) {
    case 'object':
      if (obj === null) {
        // null => null
        rv = null;
      } else {
        switch (toString.call(obj)) {
          case '[object Array]':
            // It's an array, create a new array with
            // deep copies of the entries
            rv = obj.map(deepCopyObject);
            break;
          case '[object Date]':
            // Clone the date
            rv = new Date(obj);
            break;
          case '[object RegExp]':
            // Clone the RegExp
            rv = new RegExp(obj);
            break;
          // ...probably a few others
          default:
            // Some other kind of object, deep-copy its
            // properties into a new object
            rv = Object.keys(obj).reduce(function(prev, key) {
              prev[key] = deepCopyObject(obj[key]);
              return prev;
            }, {});
            break;
        }
      }
      break;
    default:
      // It's a primitive, copy via assignment
      rv = obj;
      break;
  }
  return rv;
};

export function toRawDeep<T>(observed: T): T {
  const val = toRaw(observed);

  if (Array.isArray(val)) {
    return val.map(toRawDeep) as T;
  }

  if (val === null) return null as T;

  if (typeof val === 'object') {
    const entries = Object.entries(val).map(([key, val]) => [key, toRawDeep(val)]);

    return Object.fromEntries(entries);
  }

  return val;
}
